/**
 * Settings -> Forms -> Response sets: a named list of answers several
 * questions share. Each answer can say what it means - its outcome, a score,
 * a flag, whether it creates a follow-up action - and what it asks of the
 * person answering (a photo, a note). Only that last part reaches them.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import {
  useSaveFormResponseSet,
  type FormResponseSet,
  type ResponseSetAnswer,
} from "@/admin/hooks/use-forms";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { toastApiError } from "@/shared/lib/api-error";
import { useForm, type FormField } from "@/shared/lib/forms";

// "Not set"; a select item cannot have an empty value.
const NONE = "none";

type Need = "photo" | "note";

/** One answer as it is saved: empty answer data left off. */
function cleanAnswer(answer: ResponseSetAnswer): ResponseSetAnswer {
  const { value, label, outcome, score, severity, creates_action, blocks, requires } = answer;
  return {
    ...answer,
    value,
    label: label.trim(),
    outcome: outcome || undefined,
    score: typeof score === "number" && Number.isFinite(score) ? score : undefined,
    severity: severity || undefined,
    creates_action: creates_action || undefined,
    blocks: blocks || undefined,
    requires: requires?.length ? requires : undefined,
  };
}

function ResponseSetAnswers({
  answers,
  onChange,
  error,
}: {
  answers: ResponseSetAnswer[];
  onChange: (answers: ResponseSetAnswer[]) => void;
  error?: string;
}) {
  const { t } = useTranslation();
  const update = (index: number, patch: Partial<ResponseSetAnswer>) =>
    onChange(answers.map((answer, i) => (i === index ? { ...answer, ...patch } : answer)));
  const toggleNeed = (index: number, need: Need, on: boolean) => {
    const current = answers[index].requires ?? [];
    update(index, {
      requires: on ? [...current.filter((n) => n !== need), need] : current.filter((n) => n !== need),
    });
  };
  return (
    <div className="flex flex-col gap-2" data-testid="response-set-answers">
      <Label className="block clara-label font-normal">
        {t("forms.settings.answers", "Answers")}
      </Label>
      <p className="text-[12px] text-[var(--ink-3)]">
        {t(
          "forms.settings.answersHint",
          "The person answering sees the answers and what each one asks of them. Outcome, score, flag and actions stay with your team.",
        )}
      </p>
      <ol className="flex flex-col gap-2">
        {answers.map((answer, index) => (
          <li
            key={index}
            className="flex flex-col gap-2 rounded-lg border-[0.5px] border-[var(--rule)] p-3"
            data-testid={`response-set-answer-${index}`}
          >
            <div className="flex items-center gap-1">
              <Input
                value={answer.label}
                onChange={(event) => update(index, { label: event.target.value })}
                placeholder={t("forms.settings.answerLabelPlaceholder", "e.g. Pass")}
                aria-label={t("forms.settings.answerLabel", "Answer")}
                aria-invalid={Boolean(error) && !answer.label.trim()}
                className="flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => onChange(answers.filter((_, i) => i !== index))}
                aria-label={t("forms.settings.removeAnswer", "Remove answer")}
              >
                <X size={14} />
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <Select
                value={answer.outcome ?? NONE}
                onValueChange={(value: string) =>
                  update(index, {
                    outcome: value === NONE ? undefined : (value as ResponseSetAnswer["outcome"]),
                  })
                }
              >
                <SelectTrigger className="w-full" aria-label={t("forms.settings.outcome", "Outcome")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t("forms.settings.outcomeNone", "No outcome")}</SelectItem>
                  <SelectItem value="pass">{t("forms.settings.outcomePass", "Pass")}</SelectItem>
                  <SelectItem value="fail">{t("forms.settings.outcomeFail", "Fail")}</SelectItem>
                  <SelectItem value="na">
                    {t("forms.settings.outcomeNa", "Not applicable")}
                  </SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={answer.severity ?? NONE}
                onValueChange={(value: string) =>
                  update(index, {
                    severity: value === NONE ? undefined : (value as ResponseSetAnswer["severity"]),
                  })
                }
              >
                <SelectTrigger className="w-full" aria-label={t("forms.settings.flag", "Flag")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t("forms.settings.flagNone", "No flag")}</SelectItem>
                  <SelectItem value="yellow">{t("forms.settings.flagAmber", "Amber flag")}</SelectItem>
                  <SelectItem value="red">{t("forms.settings.flagRed", "Red flag")}</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="number"
                inputMode="decimal"
                value={answer.score ?? ""}
                onChange={(event) =>
                  update(index, {
                    score: event.target.value === "" ? undefined : Number(event.target.value),
                  })
                }
                placeholder={t("forms.settings.score", "Score")}
                aria-label={t("forms.settings.score", "Score")}
              />
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              <Label className="flex items-center gap-2 text-[12px] font-normal">
                <Checkbox
                  checked={answer.requires?.includes("photo") ?? false}
                  onCheckedChange={(on) => toggleNeed(index, "photo", Boolean(on))}
                />
                {t("forms.settings.needsPhoto", "Needs a photo")}
              </Label>
              <Label className="flex items-center gap-2 text-[12px] font-normal">
                <Checkbox
                  checked={answer.requires?.includes("note") ?? false}
                  onCheckedChange={(on) => toggleNeed(index, "note", Boolean(on))}
                />
                {t("forms.settings.needsNote", "Needs a note")}
              </Label>
              <Label className="flex items-center gap-2 text-[12px] font-normal">
                <Checkbox
                  checked={answer.creates_action ?? false}
                  onCheckedChange={(on) => update(index, { creates_action: Boolean(on) })}
                />
                {t("forms.settings.createsAction", "Creates a follow-up action")}
              </Label>
              <Label className="flex items-center gap-2 text-[12px] font-normal">
                <Checkbox
                  checked={answer.blocks ?? false}
                  onCheckedChange={(on) => update(index, { blocks: Boolean(on) })}
                />
                {t("forms.settings.blocks", "Stops the work")}
              </Label>
            </div>
          </li>
        ))}
      </ol>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        onClick={() => onChange([...answers, { label: "" }])}
        data-testid="response-set-add-answer"
      >
        <Plus size={14} />
        {t("forms.settings.addAnswer", "Add answer")}
      </Button>
      {error ? <p className="text-[12px] text-[var(--status-error)]">{error}</p> : null}
    </div>
  );
}

/** Adds a response set, or edits one. */
export function FormResponseSetDialog({
  responseSet,
  onClose,
}: {
  responseSet?: FormResponseSet;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const save = useSaveFormResponseSet();

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: "text",
        name: "name",
        label: t("forms.settings.name", "Name"),
        required: t("forms.settings.nameRequired", "Name is required"),
        value: responseSet?.name ?? "",
        placeholder: t("forms.settings.responseSetNamePlaceholder", "e.g. Pass, advisory or fail"),
        testId: "response-set-name",
      },
      {
        type: "switch",
        name: "is_active",
        label: t("forms.settings.active", "Active"),
        hint: t(
          "forms.settings.responseSetActiveHint",
          "Only active response sets can be picked for a question.",
        ),
        value: responseSet?.is_active ?? true,
      },
      {
        type: "custom",
        name: "options",
        label: t("forms.settings.answers", "Answers"),
        value: responseSet?.options ?? [{ label: "" }],
        validationSchema: z
          .array(z.object({ label: z.string() }).passthrough())
          .min(1, t("forms.settings.answersRequired", "Add at least one answer"))
          .refine(
            (answers) => answers.every((answer) => answer.label.trim()),
            t("forms.settings.answerLabelRequired", "Give every answer a label"),
          ),
        render: (ctx) => (
          <ResponseSetAnswers
            answers={(ctx.values.options ?? []) as ResponseSetAnswer[]}
            onChange={(next) => {
              ctx.setFieldValue("options", next);
              // Setting the value does not re-check it; clear a fixed error.
              if (next.length > 0 && next.every((answer) => answer.label.trim())) {
                ctx.setFieldError("options", undefined);
              }
            }}
            error={ctx.errors.options as string | undefined}
          />
        ),
      },
    ],
    [responseSet, t],
  );

  const { Form, ref } = useForm(
    { name: "form-response-set", submit: () => <></>, fields },
    async (values) => {
      try {
        await save.mutateAsync({
          key: responseSet?.key,
          name: String(values.name || "").trim(),
          is_active: Boolean(values.is_active),
          options: ((values.options ?? []) as ResponseSetAnswer[]).map(cleanAnswer),
        });
        toast.success(t("forms.settings.responseSetSaved", "Response set saved"));
        onClose();
      } catch (err) {
        toastApiError(
          err,
          t("forms.settings.responseSetSaveFailed", "Could not save the response set."),
        );
      }
    },
    [responseSet, t],
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[560px]" data-testid="response-set-dialog">
        <DialogHeader>
          <DialogTitle>
            {responseSet?.name ?? t("forms.settings.newResponseSet", "New response set")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "forms.settings.responseSetDialogHint",
              "Questions that use this set ask these answers. Changing it changes them all.",
            )}
          </DialogDescription>
        </DialogHeader>
        <Form className="flex flex-col gap-3" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={() => ref.current?.submitForm()}
            disabled={save.isPending}
            data-testid="response-set-save"
          >
            {save.isPending
              ? t("forms.settings.saving", "Saving...")
              : t("common.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
